"use client";

import type { ReactNode } from "react";

import { useMinimapWidth, usePianoHeight } from "@/contexts/studio-context";

type DragKind = "midi" | "musicxml" | "pdf" | null;

type StudioDropOverlayProps = {
  /**
   * Drag-over state piped from the window file drop zone:
   *   - `null`       : no drag in flight
   *   - `"midi"`     : drag identified as MIDI (or unknown / generic)
   *   - `"musicxml"` : drag identified as an engraved score file
   *   - `"pdf"`      : drag identified as PDF (sheet music OCR pipeline)
   * The browser security model hides the file name during dragover, so
   * the parent typically passes `"midi"` (the dominant case) and the
   * mismatch is detected at drop time.
   */
  dragKind?: DragKind;
  /** True for ~2.5 s after dropping a file with an unsupported extension. */
  errorActive?: boolean;
  /**
   * Which refusal to word. `"musescore"` is the one file we recognise well
   * enough to say what to do with it; everything else falls back to naming
   * the formats the studio reads.
   */
  errorKind?: DropErrorKind;
};

export type DropErrorKind = "unknown" | "musescore";

const COLORS = {
  text: "#e8e8ed",
  textDim: "#7a7a85",
  violet: "#8b5cf6",
  violetLight: "#c4b5fd",
  violetSoft: "rgba(139, 92, 246, 0.14)",
  violetGlow: "rgba(139, 92, 246, 0.32)",
  red: "#f87171",
  redGlow: "rgba(248, 113, 113, 0.4)",
};

/**
 * Drop-only overlay, renders the dashed frame and the drag/error copy
 * around the piano-roll. Decoupled from `EmptyStudioOverlay` so the
 * import affordance is also available when a piece is already loaded
 * (free play mode), not just on the empty state.
 *
 * Renders nothing while `dragKind === null && !errorActive`, the
 * overlay is mounted by the parent based on `dropEnabled`, and this
 * component just decides whether to paint anything.
 */
export function StudioDropOverlay({
  dragKind = null,
  errorActive = false,
  errorKind = "unknown",
}: StudioDropOverlayProps) {
  const showError = errorActive;
  const showDrag = !showError && dragKind !== null;
  const pianoHeight = usePianoHeight();
  const minimapWidth = useMinimapWidth();

  if (!showError && !showDrag) {
    return null;
  }

  return (
    <div
      aria-hidden="true"
      className="absolute"
      style={{
        // Anchor the overlay to the *roll only*, exclude the bottom
        // piano keyboard and the right-side minimap, both of which the
        // user must keep clickable. Mêmes mesures vivantes que le slot
        // overlay de `StudioFrame`, publiées par la vue Pixi.
        top: 0,
        left: 0,
        right: minimapWidth,
        bottom: pianoHeight,
        // Soft scrim, keeps the drop framing readable when the
        // piano-roll has dense content underneath.
        backgroundColor: "rgba(7, 7, 9, 0.55)",
        backdropFilter: "blur(2px) saturate(80%)",
        WebkitBackdropFilter: "blur(2px) saturate(80%)",
        // Sit above the studio overlay slot (z-24) so the drop framing
        // is always visible, even when an overlay is up underneath.
        // The right panel is z-30, modals (upload/practice) are higher
        // still, so they remain on top as expected.
        zIndex: 25,
        pointerEvents: "none",
      }}
    >
      <DropFrame tone={showError ? "red" : "violet"} />

      {showDrag && <DragoverContent kind={dragKind ?? "midi"} />}
      {showError && <ErrorContent kind={errorKind} />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Drag-over content, bobbing glyph + "Lâche pour importer"
// ─────────────────────────────────────────────────────────────

type DragoverContentProps = {
  kind: Exclude<DragKind, null>;
};

const DRAGOVER_COPY: Record<
  Exclude<DragKind, null>,
  { label: string; subline: string }
> = {
  midi: {
    label: ".MID / .MIDI",
    subline: "On le découpe en phrases et tu commences à apprendre.",
  },
  musicxml: {
    label: ".MUSICXML / .MXL",
    subline: "Les mains sont écrites sur les portées, on les lit telles quelles.",
  },
  pdf: {
    label: ".PDF",
    subline: "On reconnaît la partition, puis tu choisis les mains à travailler.",
  },
};

function DragoverContent({ kind }: DragoverContentProps) {
  const { label, subline } = DRAGOVER_COPY[kind];

  return (
    <div
      className="absolute inset-0 flex items-center justify-center"
      style={{ pointerEvents: "none" }}
    >
      <div className="text-center">
        <div
          className="mx-auto flex items-center justify-center"
          style={{
            width: 64,
            height: 64,
            borderRadius: 16,
            marginBottom: 22,
            background: "rgba(139, 92, 246, 0.12)",
            border: `1px solid ${COLORS.violetGlow}`,
            color: COLORS.violetLight,
            boxShadow: `0 0 30px ${COLORS.violetGlow}, inset 0 1px 0 rgba(255,255,255,0.06)`,
            animation: "eo-bob 1.6s ease-in-out infinite",
          }}
        >
          <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M12 4v12" />
            <path d="M6 10l6-6 6 6" />
            <path d="M4 20h16" />
          </svg>
        </div>

        <div
          className="inline-flex items-center"
          style={{
            gap: 8,
            padding: "5px 11px",
            borderRadius: 999,
            border: `1px solid ${COLORS.violetGlow}`,
            background: COLORS.violetSoft,
            fontSize: 10,
            fontFamily: "var(--font-geist-mono), ui-monospace, monospace",
            color: COLORS.violetLight,
            letterSpacing: "0.2em",
            marginBottom: 18,
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: 5,
              height: 5,
              borderRadius: 99,
              background: COLORS.violet,
              boxShadow: `0 0 10px ${COLORS.violet}`,
              animation: "eo-pulse 1.2s ease-in-out infinite",
            }}
          />
          {label} DÉTECTÉ
        </div>

        <div
          style={{
            fontSize: 36,
            fontWeight: 250,
            color: COLORS.text,
            letterSpacing: "-0.025em",
            lineHeight: 1.15,
            marginBottom: 10,
          }}
        >
          Lâche pour{" "}
          <span style={{ color: COLORS.violetLight, fontStyle: "italic" }}>
            importer
          </span>
          .
        </div>
        <div
          className="mx-auto"
          style={{
            fontSize: 14,
            color: COLORS.textDim,
            lineHeight: 1.5,
            maxWidth: 380,
          }}
        >
          {subline}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Error content, unsupported file format
// ─────────────────────────────────────────────────────────────

/**
 * What each refusal says.
 *
 * A MuseScore project is not an unreadable file, it is a readable file in the
 * wrong wrapper, and the copy has to reflect that: the user is one export away
 * and needs to be told which one. The generic case has nothing to offer beyond
 * the list of formats, so that is all it claims. Same message as the upload
 * modal's `MUSESCORE_EXPORT_MESSAGE`, split in two because this surface has a
 * headline.
 */
const ERROR_COPY: Record<DropErrorKind, { badge: string; title: string; hint: ReactNode }> = {
  unknown: {
    badge: "FICHIER NON RECONNU",
    title: "Ce format n'est pas supporté.",
    hint: (
      <>
        Pour l'instant le studio lit le{" "}
        <span style={{ color: COLORS.text }}>MIDI</span>, le{" "}
        <span style={{ color: COLORS.text }}>MusicXML</span> et les{" "}
        <span style={{ color: COLORS.text }}>PDF</span> de partition.
      </>
    ),
  },
  musescore: {
    badge: "PROJET MUSESCORE",
    title: "Ce format n'est pas supporté.",
    hint: (
      <>
        Dans MuseScore :{" "}
        <span style={{ color: COLORS.text }}>Fichier &gt; Exporter &gt; MusicXML</span>, puis
        redépose le fichier obtenu.
      </>
    ),
  },
};

function ErrorContent({ kind }: { kind: DropErrorKind }) {
  const copy = ERROR_COPY[kind];

  return (
    <div
      className="absolute inset-0 flex items-center justify-center"
      style={{ pointerEvents: "none" }}
    >
      <div
        className="text-center"
        style={{
          pointerEvents: "auto",
          animation: "eo-shake 0.4s ease-in-out",
        }}
      >
        <div
          className="mx-auto flex items-center justify-center"
          style={{
            width: 64,
            height: 64,
            borderRadius: 16,
            marginBottom: 22,
            background: "rgba(248, 113, 113, 0.10)",
            border: `1px solid ${COLORS.redGlow}`,
            color: COLORS.red,
            boxShadow: "0 0 30px rgba(248, 113, 113, 0.25)",
          }}
        >
          <svg
            width="26"
            height="26"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M12 8v5" />
            <path d="M12 16.5v.5" />
            <circle cx="12" cy="12" r="9.5" />
          </svg>
        </div>

        <div
          className="inline-flex items-center"
          style={{
            gap: 8,
            padding: "5px 11px",
            borderRadius: 999,
            border: "1px solid rgba(248, 113, 113, 0.35)",
            background: "rgba(248, 113, 113, 0.08)",
            fontSize: 10,
            fontFamily: "var(--font-geist-mono), ui-monospace, monospace",
            color: "#fca5a5",
            letterSpacing: "0.2em",
            marginBottom: 18,
          }}
        >
          {copy.badge}
        </div>

        <div
          style={{
            fontSize: 32,
            fontWeight: 250,
            color: COLORS.text,
            letterSpacing: "-0.02em",
            lineHeight: 1.2,
            marginBottom: 8,
          }}
        >
          {copy.title}
        </div>
        <div
          className="mx-auto"
          style={{
            fontSize: 14,
            color: COLORS.textDim,
            lineHeight: 1.5,
            maxWidth: 380,
          }}
        >
          {copy.hint}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Drop frame, dashed glow ring around the canvas
// ─────────────────────────────────────────────────────────────

type DropFrameProps = {
  tone: "violet" | "red";
};

function DropFrame({ tone }: DropFrameProps) {
  const palette =
    tone === "violet"
      ? {
          stroke: COLORS.violet,
          glow: COLORS.violetGlow,
          halo: "rgba(139, 92, 246, 0.18)",
        }
      : {
          stroke: COLORS.red,
          glow: COLORS.redGlow,
          halo: "rgba(248, 113, 113, 0.16)",
        };

  return (
    <>
      {/* Halo radial behind the frame */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(ellipse 65% 55% at 50% 55%, ${palette.halo}, transparent 70%)`,
        }}
      />
      {/* Dashed glowing frame */}
      <div
        className="absolute pointer-events-none"
        style={{
          inset: 16,
          borderRadius: 14,
          border: `2px dashed ${palette.stroke}`,
          boxShadow: `0 0 32px ${palette.glow}, inset 0 0 32px ${palette.halo}`,
        }}
      />
      {/* Accentuated corners, 4 hard L-shapes anchoring the frame */}
      <DropFrameCorner position="top-left" stroke={palette.stroke} glow={palette.glow} />
      <DropFrameCorner position="top-right" stroke={palette.stroke} glow={palette.glow} />
      <DropFrameCorner position="bottom-left" stroke={palette.stroke} glow={palette.glow} />
      <DropFrameCorner position="bottom-right" stroke={palette.stroke} glow={palette.glow} />
    </>
  );
}

type DropFrameCornerProps = {
  position: "top-left" | "top-right" | "bottom-left" | "bottom-right";
  stroke: string;
  glow: string;
};

function DropFrameCorner({ position, stroke, glow }: DropFrameCornerProps) {
  const isTop = position.startsWith("top");
  const isLeft = position.endsWith("left");

  const positionStyle: React.CSSProperties = {
    position: "absolute",
    width: 28,
    height: 28,
    pointerEvents: "none",
    boxShadow: `0 0 14px ${glow}`,
  };

  if (isTop) {
    positionStyle.top = 16;
  } else {
    positionStyle.bottom = 16;
  }

  if (isLeft) {
    positionStyle.left = 16;
  } else {
    positionStyle.right = 16;
  }

  if (isTop && isLeft) {
    positionStyle.borderTop = `2px solid ${stroke}`;
    positionStyle.borderLeft = `2px solid ${stroke}`;
    positionStyle.borderRadius = "14px 0 0 0";
  } else if (isTop && !isLeft) {
    positionStyle.borderTop = `2px solid ${stroke}`;
    positionStyle.borderRight = `2px solid ${stroke}`;
    positionStyle.borderRadius = "0 14px 0 0";
  } else if (!isTop && isLeft) {
    positionStyle.borderBottom = `2px solid ${stroke}`;
    positionStyle.borderLeft = `2px solid ${stroke}`;
    positionStyle.borderRadius = "0 0 0 14px";
  } else {
    positionStyle.borderBottom = `2px solid ${stroke}`;
    positionStyle.borderRight = `2px solid ${stroke}`;
    positionStyle.borderRadius = "0 0 14px 0";
  }

  return <div aria-hidden="true" style={positionStyle} />;
}
